// ── Finding the user's location ─────────────────────────────────────────────────
// One implementation for every "📍 Use my current location" button. There were six copies, each
// carrying the same two faults:
//
//   1. getCurrentPosition was called with NO options. The spec's default timeout is Infinity, so on
//      a device that can't get a fix — indoors, GPS off, a desktop with no wifi positioning — the
//      success callback never fires and neither does the error one. The button sits on "Detecting…"
//      forever and the user reports that location "doesn't work". It hadn't failed; it had hung.
//
//   2. Reverse geocoding went to Nominatim from the browser. That is now a hard 403 with no
//      Access-Control-Allow-Origin header — the OSM Foundation's usage policy requires an
//      identifying User-Agent, and a browser is forbidden from setting one, so this can never work
//      client-side. Don't "fix" it by putting the header back; fetch silently drops it.
//      BigDataCloud's reverse-geocode-client endpoint is built for exactly this: CORS-enabled, no
//      API key, no server round trip.
//
// Everything here degrades rather than throws away work: if the place lookup fails we still return
// the coordinates, because coordinates alone are enough for distance sorting and the user can type
// their city.

export const GEO_TIMEOUT_MS = 12_000;
const GEOCODE_TIMEOUT_MS = 8_000;

// Coordinates only. Rejects with the browser's GeolocationPositionError (which carries .code), or an
// Error with .code = 0 for the cases the browser doesn't model.
export function getPosition({ timeout = GEO_TIMEOUT_MS, highAccuracy = true, maxAgeMs = 300_000 } = {}) {
  return new Promise((resolve, reject) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      reject(Object.assign(new Error('unsupported'), { code: 0 }));
      return;
    }
    // Geolocation is a secure-context API. Over plain http it fails in a way that looks like a
    // permission denial, which sends people to fiddle with settings that were never the problem.
    if (typeof window !== 'undefined' && window.isSecureContext === false) {
      reject(Object.assign(new Error('insecure'), { code: -1 }));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
        accuracy: pos.coords.accuracy ?? null,
      }),
      (err) => reject(err),
      // The timeout is the point of this file.
      { enableHighAccuracy: highAccuracy, timeout, maximumAge: maxAgeMs }
    );
  });
}

// Coordinates → a place. Returns null on any failure; callers must cope with that.
export async function reverseGeocode(lat, lng) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), GEOCODE_TIMEOUT_MS);
  try {
    const res = await fetch(
      `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lng}&localityLanguage=en`,
      { signal: controller.signal }
    );
    if (!res.ok) return null;
    const d = await res.json();

    const admin = Array.isArray(d?.localityInfo?.administrative) ? d.localityInfo.administrative : [];
    const city = d.city || d.locality || [...admin].sort((a, b) => (b.adminLevel || 0) - (a.adminLevel || 0))
      .find(a => (a.adminLevel || 0) >= 5)?.name || '';
    // The most specific named area below the city, which is what the "building / area" field wants.
    const finest = [...admin].sort((a, b) => (b.adminLevel || 0) - (a.adminLevel || 0))[0]?.name || '';
    const building = d.locality && d.locality !== city ? d.locality : (finest && finest !== city ? finest : '');

    return {
      city,
      building,
      neighborhood: building,
      postalCode: d.postcode || '',
      country: d.countryName || '',
      countryCode: d.countryCode || '',
      state: d.principalSubdivision || '',
    };
  } catch {
    return null;   // aborted, offline, rate-limited, or a shape we don't recognise
  } finally {
    clearTimeout(timer);
  }
}

// What went wrong, in words a user can act on. The old code said "access was denied" for every
// failure, which is actively misleading when the real cause was a timeout.
export function geoErrorMessage(err) {
  switch (err?.code) {
    case 1: // PERMISSION_DENIED
      return 'Location permission was blocked. Allow location for this site in your browser settings, then try again.';
    case 2: // POSITION_UNAVAILABLE
      return "Your device couldn't work out where it is. Check that location services are on, or type your city instead.";
    case 3: // TIMEOUT
      return 'Finding your location took too long. Move somewhere with a clearer signal and try again, or type your city instead.';
    case -1:
      return 'Location needs a secure (https) connection.';
    default:
      return 'Your browser does not support locating you. Please type your city instead.';
  }
}

// The whole job: coordinates plus, best effort, a place. Never throws.
// → { ok, coords, place, error }  — `ok` means we got coordinates, not that geocoding worked.
export async function detectLocation(opts) {
  let coords;
  try {
    coords = await getPosition(opts);
  } catch (err) {
    return { ok: false, coords: null, place: null, error: geoErrorMessage(err) };
  }
  const place = await reverseGeocode(coords.lat, coords.lng);
  return { ok: true, coords, place, error: null };
}
